import { Injectable } from '@angular/core';
import { AutorizacaoLojaService } from './autorizacao-loja.service';

declare global {
  interface Window {
    op: any;
    openpanel: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class OpenpanelAnalyticsService {
  private readonly visitorKey = 'openpanel_visitor_id';
  private readonly sessionKey = 'openpanel_visit_session';
  private readonly sessionTimeoutMs = 30 * 60 * 1000;
  private readonly attributionFirstTouchKey = 'openpanel_attribution_first_touch';
  private readonly attributionLastTouchKey = 'openpanel_attribution_last_touch';
  private readonly knownCustomersKey = 'openpanel_known_customers';
  private readonly defaultScriptUrl = 'https://openpanel.dev/op1.js';
  private clientIdAtual: string = null;
  private habilitado = false;
  private empresaAtual: any = null;
  private serverSessionId: string = null;

  constructor(private autorizacaoLojaService: AutorizacaoLojaService) {
    // A sessão do backend (cookie) persiste por dias no mesmo navegador — se ela
    // virasse o sessionId dos eventos, visitas distintas do mesmo cliente colariam
    // numa só no funil. Ela segue nos eventos em campo próprio (serverSessionId).
    this.autorizacaoLojaService.sessao$.subscribe((sessao: string) => {
      if (!sessao) {
        return;
      }

      this.serverSessionId = sessao;
    });
  }

  init(empresa: any) {
    const config = this.obtenhaConfig(empresa);
    config.clientId = String(config.clientId || '').trim();
    this.empresaAtual = empresa;
    this.sincronizeAtribuicao();

    if (!config.clientId) {
      this.habilitado = false;
      return;
    }

    const sdkClientId = String(window.openpanel?.options?.clientId || '').trim();
    const initNaFila = window.op?.q?.find((chamada: any[]) => chamada?.[0] === 'init');
    const clientIdNaFila = String(initNaFila?.[1]?.clientId || '').trim();

    if ((sdkClientId && sdkClientId !== config.clientId) ||
        (clientIdNaFila && clientIdNaFila !== config.clientId)) {
      this.reinicieSdk();
    }

    if (!window.openpanel) {
      this.garantaQueueGlobal();
      window.op('init', {
        clientId: config.clientId,
        apiUrl: config.apiUrl || undefined,
        trackScreenViews: true,
        trackOutgoingLinks: false,
        trackAttributes: true
      });
      this.carregueScript(config.scriptUrl || this.defaultScriptUrl);
    }

    this.clientIdAtual = config.clientId;
    this.habilitado = true;
    window.op('setGlobalProperties', this.obtenhaPropriedadesGlobais(empresa));

    const usuario = this.autorizacaoLojaService.getUsuario();

    if (usuario) {
      this.identifiqueContatoSePossivel(usuario, {
        etapaFunil: 'session_init'
      });
    }
  }

  track(evento: string, propriedades: any = {}): boolean {
    if (!this.habilitado || !evento || !window.op) {
      return false;
    }

    this.sincronizeAtribuicao();

    const payload = this.removaValoresVazios({
      ...this.obtenhaContextoBase(),
      ...propriedades
    });

    window.op('track', evento, payload);
    return true;
  }

  obtenhaIdSessaoVisita(): string {
    return this.obtenhaSessionId();
  }

  identify(contato: any, extras: any = {}) {
    if (!this.habilitado || !contato || !window.op) {
      return;
    }

    const profileId = this.obtenhaProfileId(contato);

    if (!profileId) {
      return;
    }

    const nome = contato.nome ? String(contato.nome).trim() : '';
    const partesNome = nome.split(' ').filter((item: string) => !!item);

    window.op('identify', this.removaValoresVazios({
      profileId: profileId,
      firstName: partesNome.length ? partesNome[0] : undefined,
      lastName: partesNome.length > 1 ? partesNome.slice(1).join(' ') : undefined,
      email: contato.email || undefined,
      phone: this.normalizePhone(contato.telefone || contato.whatsapp || contato.numero),
      properties: this.removaValoresVazios({
        ...this.obtenhaContextoBase(),
        customerStatus: this.obtenhaStatusCliente(contato),
        ...extras
      })
    }));
  }

  trackMenuOpen(empresa: any, pedido: any, contexto: any = {}) {
    this.track('menu_open', {
      ...this.obtenhaContextoEmpresa(empresa),
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      snapshotReason: 'menu_open',
      ...contexto
    });
  }

  trackCategoryView(categoria: any, contexto: any = {}) {
    this.track('category_view', {
      categoryId: categoria?.id,
      categoryName: categoria?.nome,
      categoryPosition: categoria?.posicao,
      categoryLevel: categoria?.nivel,
      categoryParentId: categoria?.categoriaPai?.id,
      categoryParentName: categoria?.categoriaPai?.nome,
      ...contexto
    });
  }

  trackProductView(produto: any, contexto: any = {}) {
    this.track('product_view', {
      ...this.obtenhaContextoProduto(produto),
      ...contexto
    });
  }

  trackSearch(termo: string, contexto: any = {}) {
    this.track('search', {
      query: termo,
      queryLength: termo ? String(termo).trim().length : 0,
      ...contexto
    });
  }

  trackAddToCart(item: any, pedido: any, contexto: any = {}) {
    this.track('product_add_cart', {
      ...this.obtenhaContextoItem(item),
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });
  }

  trackRemoveFromCart(item: any, pedido: any, contexto: any = {}) {
    this.track('product_remove_cart', {
      ...this.obtenhaContextoItem(item),
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });
  }

  trackCartView(pedido: any, contexto: any = {}) {
    this.track('cart_view', {
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });
  }

  trackCheckoutStart(pedido: any, contexto: any = {}) {
    this.identifiqueContatoSePossivel(pedido?.contato, {
      canal: this.obtenhaCanal(pedido),
      etapaFunil: 'checkout_start'
    });

    this.track('checkout_start', {
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });
  }

  trackPaymentInfo(pedido: any, contexto: any = {}) {
    this.identifiqueContatoSePossivel(pedido?.contato, {
      canal: this.obtenhaCanal(pedido),
      etapaFunil: 'payment_info'
    });

    this.track('payment_info_added', {
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });
  }

  trackPaymentError(pedido: any, erro: any, contexto: any = {}) {
    const mensagem = typeof erro === 'string'
      ? erro
      : erro?.message || erro?.erro || erro?.error?.message || null;

    this.track('payment_error', {
      ...this.obtenhaResumoPedido(pedido),
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      errorMessage: mensagem,
      errorCode: erro?.code || erro?.codigo || erro?.status || erro?.error?.code,
      errorType: erro?.name || erro?.tipo || erro?.error?.type,
      ...contexto
    });
  }

  trackOrderComplete(pedido: any, resposta: any = {}, contexto: any = {}): boolean {
    this.identifiqueContatoSePossivel(pedido?.contato, {
      canal: this.obtenhaCanal(pedido),
      etapaFunil: 'order_complete',
      lastOrderId: resposta?.id || pedido?.id,
      lastOrderCode: resposta?.codigo || pedido?.codigo
    });

    const rastreou = this.track('order_complete', {
      ...this.obtenhaResumoPedido(pedido),
      orderId: resposta?.id || pedido?.id,
      orderCode: resposta?.codigo || pedido?.codigo,
      guid: resposta?.guid || pedido?.guid,
      ...this.obtenhaContextoContato(pedido?.contato),
      canal: this.obtenhaCanal(pedido),
      ...contexto
    });

    this.marqueClienteComoRecorrente(pedido?.contato);
    return rastreou;
  }

  trackOutgoingLink(href: string, texto: string = '', contexto: any = {}) {
    const tipo = this.obtenhaTipoLink(href);

    this.track('outgoing_link', {
      href: href,
      label: texto,
      linkType: tipo,
      isWhatsapp: tipo === 'whatsapp',
      isPhone: tipo === 'telefone',
      ...contexto
    });
  }

  private obtenhaConfig(empresa: any) {
    if (!empresa) {
      return {};
    }

    return empresa.openpanel || {
      apiUrl: empresa.openpanelApiUrl,
      scriptUrl: empresa.openpanelScriptUrl,
      clientId: empresa.openpanelClientId
    };
  }

  private obtenhaPropriedadesGlobais(empresa: any) {
    return this.removaValoresVazios({
      ...this.obtenhaContextoBase(),
      ...this.obtenhaContextoEmpresa(empresa),
      origem: 'loja-web'
    });
  }

  private obtenhaProfileId(contato: any) {
    return contato?.id ||
      this.normalizePhone(contato?.telefone || contato?.whatsapp || contato?.numero) ||
      contato?.email ||
      null;
  }

  private normalizePhone(valor: string) {
    if (!valor) {
      return null;
    }

    const digitos = String(valor).replace(/\D/g, '');
    return digitos || null;
  }

  private obtenhaVisitorId() {
    let visitorId = localStorage.getItem(this.visitorKey);

    if (!visitorId) {
      visitorId = `mc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(this.visitorKey, visitorId);
    }

    return visitorId;
  }

  // Sessão de VISITA: compartilhada entre abas e rotacionada após 30min sem
  // eventos (padrão de analytics) — é a chave de deduplicação do funil.
  private obtenhaSessionId() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const agora = Date.now();
    let registro: any = null;

    try {
      registro = JSON.parse(localStorage.getItem(this.sessionKey));
    } catch (erro) {
      registro = null;
    }

    const ultimaAtividade = Number(registro?.ts);

    if (!registro?.id || !Number.isFinite(ultimaAtividade) || agora - ultimaAtividade > this.sessionTimeoutMs) {
      registro = { id: `mc_session_${agora}_${Math.random().toString(36).slice(2, 10)}`, ts: agora };
    }

    registro.ts = agora;
    localStorage.setItem(this.sessionKey, JSON.stringify(registro));

    return registro.id;
  }

  private obtenhaContextoBase() {
    return this.removaValoresVazios({
      visitorId: this.obtenhaVisitorId(),
      sessionId: this.obtenhaSessionId(),
      serverSessionId: this.serverSessionId || undefined,
      origem: 'loja-web',
      ...this.obtenhaContextoEmpresa(this.empresaAtual),
      ...this.obtenhaContextoPagina(),
      ...this.obtenhaAtribuicao(),
      ...this.obtenhaContextoDispositivo()
    });
  }

  private obtenhaContextoEmpresa(empresa: any = this.empresaAtual) {
    return {
      empresaId: empresa?.id,
      empresaNome: empresa?.nome,
      dominio: empresa?.dominio,
      tipoLoja: empresa?.tipoDeLoja,
      segmento: empresa?.segmento,
      unidadeId: empresa?.unidade?.id || empresa?.empresaMatriz?.id
    };
  }

  private obtenhaContextoPagina() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return {};
    }

    return {
      href: window.location.href,
      path: window.location.pathname,
      search: window.location.search || undefined,
      hash: window.location.hash || undefined,
      title: document.title || undefined,
      referrer: document.referrer || undefined
    };
  }

  private obtenhaContextoDispositivo() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return {};
    }

    const userAgent = navigator.userAgent || '';
    const viewportWidth = window.innerWidth || undefined;

    return {
      deviceType: this.obtenhaTipoDispositivo(viewportWidth, userAgent),
      browser: this.obtenhaBrowser(userAgent),
      os: this.obtenhaSistemaOperacional(userAgent),
      userAgent: userAgent || undefined,
      language: navigator.language || undefined,
      viewportWidth,
      viewportHeight: window.innerHeight || undefined
    };
  }

  private obtenhaAtribuicao() {
    const atual = this.obtenhaTouchAtual();
    const primeiro = this.leiaStorageJson(this.attributionFirstTouchKey);
    const ultimo = this.leiaStorageJson(this.attributionLastTouchKey);

    const touchAtual = this.removaValoresVazios({
      source: atual.source,
      medium: atual.medium,
      campaign: atual.campaign,
      content: atual.content,
      term: atual.term,
      referrerDomain: atual.referrerDomain,
      landingPath: atual.landingPath,
      gclid: atual.gclid,
      fbclid: atual.fbclid
    });

    const firstTouch = this.removaValoresVazios({
      firstSource: primeiro?.source,
      firstMedium: primeiro?.medium,
      firstCampaign: primeiro?.campaign,
      firstContent: primeiro?.content,
      firstTerm: primeiro?.term,
      firstReferrerDomain: primeiro?.referrerDomain,
      firstLandingPath: primeiro?.landingPath
    });

    const lastTouch = this.removaValoresVazios({
      lastSource: ultimo?.source,
      lastMedium: ultimo?.medium,
      lastCampaign: ultimo?.campaign,
      lastContent: ultimo?.content,
      lastTerm: ultimo?.term,
      lastReferrerDomain: ultimo?.referrerDomain,
      lastLandingPath: ultimo?.landingPath
    });

    return {
      ...touchAtual,
      ...firstTouch,
      ...lastTouch
    };
  }

  private sincronizeAtribuicao() {
    if (typeof window === 'undefined') {
      return;
    }

    const atual = this.obtenhaTouchAtual();
    const possuiPrimeiroTouch = !!this.leiaStorageJson(this.attributionFirstTouchKey);

    if (!possuiPrimeiroTouch) {
      this.salveStorageJson(this.attributionFirstTouchKey, atual);
    }

    this.salveStorageJson(this.attributionLastTouchKey, atual);
  }

  private obtenhaTouchAtual() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return {};
    }

    const params = new URLSearchParams(window.location.search || '');
    const referrer = document.referrer || '';
    const referrerDomain = this.obtenhaDominio(referrer);
    const source = this.obtenhaPrimeiroValor([
      params.get('utm_source'),
      params.get('source'),
      params.get('src'),
      referrerDomain,
      'direct'
    ]);
    const medium = this.obtenhaPrimeiroValor([
      params.get('utm_medium'),
      params.get('medium'),
      referrer ? 'referral' : 'direct'
    ]);

    return this.removaValoresVazios({
      source,
      medium,
      campaign: this.obtenhaPrimeiroValor([params.get('utm_campaign'), params.get('campaign')]),
      content: this.obtenhaPrimeiroValor([params.get('utm_content'), params.get('content')]),
      term: this.obtenhaPrimeiroValor([params.get('utm_term'), params.get('term')]),
      referrer,
      referrerDomain,
      landingPath: window.location.pathname,
      landingUrl: window.location.href,
      gclid: params.get('gclid'),
      fbclid: params.get('fbclid')
    });
  }

  private obtenhaContextoProduto(produto: any) {
    return this.removaValoresVazios({
      productId: produto?.id,
      productName: produto?.nome,
      productDescription: produto?.descricao,
      productPrice: this.arredondeNumero(produto?.preco || produto?.valorMinimo || 0),
      productNewPrice: this.arredondeNumero(produto?.novoPreco),
      categoryId: produto?.categoria?.id,
      categoryName: produto?.categoria?.nome,
      companyId: produto?.empresa?.id,
      companyName: produto?.empresa?.nome,
      productType: produto?.tipoDeVenda,
      productMinQuantity: produto?.qtdeMinima,
      productMaxQuantity: produto?.qtdeMaxima,
      productHasImage: !!(produto?.linkImagem || produto?.imagens?.length),
      productImage: produto?.imagens?.[0]?.linkImagem
        ? `/images/empresa/${produto.imagens[0].linkImagem}`
        : undefined,
      productUnavailable: !!produto?.indisponivel,
      stockQuantity: produto?.estoque?.quantidade,
      categoryPosition: produto?.categoria?.posicao
    });
  }

  private obtenhaContextoItem(item: any) {
    if (!item) {
      return {};
    }

    const produto = item?.produto || {};
    const adicionais = this.obtenhaResumoAdicionais(item);
    const sabores = Array.isArray(item?.sabores) ? item.sabores : [];
    const valorItem = typeof item?.obtenhaValor === 'function' ? item.obtenhaValor() : item?.total;

    return this.removaValoresVazios({
      itemGuid: item?.guid,
      quantity: Number(item?.qtde || 0),
      itemValue: this.arredondeNumero(valorItem),
      total: this.arredondeNumero(valorItem),
      unitPrice: this.arredondeNumero(item?.valorUnitario || item?.obtenhaValorUnitario?.()),
      sizeId: item?.produtoTamanho?.id,
      sizeName: item?.produtoTamanho?.descricao || item?.produtoTamanho?.nome,
      notes: item?.observacao || undefined,
      noteLength: item?.observacao ? String(item.observacao).trim().length : 0,
      flavorsCount: sabores.length,
      flavors: sabores.map((sabor: any) => sabor?.nome).filter((nome: string) => !!nome),
      ...adicionais,
      ...this.obtenhaContextoProduto(produto)
    });
  }

  private obtenhaResumoPedido(pedido: any) {
    const itens = Array.isArray(pedido?.itens) ? pedido.itens : [];
    const quantidadeItens = itens.reduce((total: number, item: any) => total + Number(item?.qtde || 0), 0);
    const descontoTotal = Number(pedido?.desconto || 0)
      + Number(pedido?.descontoFormaDeEntrega || 0)
      + Number(pedido?.descontoFormaDePagamento || 0);

    return this.removaValoresVazios({
      orderId: pedido?.id,
      orderCode: pedido?.codigo,
      orderGuid: pedido?.guid,
      cartItems: itens.length,
      cartQuantity: this.arredondeNumero(quantidadeItens),
      cartValue: this.arredondeNumero(typeof pedido?.obtenhaValorAhPagar === 'function' ? pedido.obtenhaValorAhPagar() : pedido?.total),
      subtotal: this.arredondeNumero(pedido?.subTotal || pedido?.obtenhaSubTotal?.()),
      total: this.arredondeNumero(pedido?.total),
      discount: this.arredondeNumero(descontoTotal),
      cashbackValue: this.arredondeNumero(pedido?.cashback?.valor),
      shippingFee: this.arredondeNumero(pedido?.entrega?.taxaDeEntrega),
      couponCode: pedido?.cupom?.codigo || pedido?.codigoCupom,
      couponId: pedido?.cupom?.id,
      couponAutoApplied: pedido?.cupom?.aplicarAuto,
      deliveryType: pedido?.entrega?.formaDeEntrega,
      deliveryNeighborhood: pedido?.entrega?.endereco?.bairro || pedido?.entrega?.bairro,
      paymentMethodId: pedido?.pagamento?.formaDePagamento?.id,
      paymentMethodName: pedido?.pagamento?.formaDePagamento?.nome,
      paymentType: pedido?.pagamento?.tipoDePagamento,
      paymentOnline: !!pedido?.pagamento?.formaDePagamento?.online,
      paymentPix: !!pedido?.pagamento?.formaDePagamento?.pix,
      paymentWallet: pedido?.pagamento?.formaDePagamento?.carteiraDigital,
      paymentChangeRequested: pedido?.pagamento?.temTroco === 'sim',
      paymentChangeValue: this.arredondeNumero(pedido?.pagamento?.trocoPara),
      mesaId: pedido?.mesa?.id,
      mesaHash: pedido?.mesa?.hash,
      isMesa: !!pedido?.mesa?.id,
      items: itens.map((item: any) => this.obtenhaPayloadItemPedido(item))
    });
  }

  private obtenhaPayloadItemPedido(item: any) {
    return this.removaValoresVazios({
      itemGuid: item?.guid,
      productId: item?.produto?.id,
      productName: item?.produto?.nome,
      categoryId: item?.produto?.categoria?.id,
      categoryName: item?.produto?.categoria?.nome,
      quantity: Number(item?.qtde || 0),
      unitPrice: this.arredondeNumero(item?.valorUnitario || item?.obtenhaValorUnitario?.()),
      total: this.arredondeNumero(item?.total || item?.obtenhaValor?.()),
      sizeId: item?.produtoTamanho?.id,
      sizeName: item?.produtoTamanho?.descricao || item?.produtoTamanho?.nome,
      flavors: Array.isArray(item?.sabores) ? item.sabores.map((sabor: any) => sabor?.nome).filter((nome: string) => !!nome) : [],
      additionalCount: this.obtenhaResumoAdicionais(item).additionalCount,
      additionalValue: this.obtenhaResumoAdicionais(item).additionalValue
    });
  }

  private obtenhaResumoAdicionais(item: any) {
    const adicionais = item?.adicionais || {};
    let additionalCount = 0;

    Object.keys(adicionais).forEach((chave: string) => {
      if (chave.startsWith('meta_')) {
        return;
      }

      const valor = adicionais[chave];

      if (valor == null) {
        return;
      }

      if (Array.isArray(valor)) {
        additionalCount += valor.length;
        return;
      }

      if (typeof valor === 'object') {
        if (Array.isArray(valor?.itensSelecionados)) {
          additionalCount += valor.itensSelecionados.length;
          return;
        }

        if (valor.totalSelecionado != null) {
          additionalCount += Number(valor.totalSelecionado || 0);
          return;
        }
      }

      additionalCount += 1;
    });

    return this.removaValoresVazios({
      additionalCount,
      additionalValue: this.arredondeNumero(item?.obtenhaValorAdicionais?.()),
      hasCustomization: additionalCount > 0 || !!item?.produtoTamanho || (Array.isArray(item?.sabores) && item.sabores.length > 0)
    });
  }

  private obtenhaContextoContato(contato: any) {
    if (!contato) {
      return {};
    }

    const telefone = this.normalizePhone(contato?.telefone || contato?.whatsapp || contato?.numero);

    return this.removaValoresVazios({
      customerId: contato?.id,
      customerName: contato?.nome,
      customerPhone: telefone,
      customerEmail: contato?.email,
      customerDocument: contato?.cpf || contato?.cnpj,
      customerStatus: this.obtenhaStatusCliente(contato),
      customerLoggedIn: !!this.autorizacaoLojaService.getUsuario(),
      customerHasPhone: !!telefone
    });
  }

  private identifiqueContatoSePossivel(contato: any, extras: any = {}) {
    if (!contato) {
      return;
    }

    this.identify(contato, {
      ...this.obtenhaContextoContato(contato),
      ...extras
    });
  }

  private obtenhaStatusCliente(contato: any) {
    const chave = this.obtenhaChaveCliente(contato);

    if (!chave) {
      return 'desconhecido';
    }

    const conhecidos = this.leiaStorageJson(this.knownCustomersKey) || {};
    return conhecidos[chave] ? 'recorrente' : 'novo';
  }

  private marqueClienteComoRecorrente(contato: any) {
    const chave = this.obtenhaChaveCliente(contato);

    if (!chave) {
      return;
    }

    const conhecidos = this.leiaStorageJson(this.knownCustomersKey) || {};
    conhecidos[chave] = Date.now();
    this.salveStorageJson(this.knownCustomersKey, conhecidos);
  }

  private obtenhaChaveCliente(contato: any) {
    if (!contato) {
      return null;
    }

    const telefone = this.normalizePhone(contato?.telefone || contato?.whatsapp || contato?.numero);
    return contato?.id || telefone || contato?.email || null;
  }

  private obtenhaCanal(pedido: any = null) {
    if (pedido?.mesa?.id) {
      return 'mesa';
    }

    const search = typeof window !== 'undefined' ? window.location.search || '' : '';
    const params = new URLSearchParams(search);

    if (params.get('hm') || params.get('qr') || params.get('qrcode')) {
      return 'qr';
    }

    return 'web';
  }

  private obtenhaTipoLink(href: string) {
    const valor = String(href || '').toLowerCase();

    if (valor.includes('wa.me') || valor.includes('whatsapp')) {
      return 'whatsapp';
    }

    if (valor.startsWith('tel:')) {
      return 'telefone';
    }

    return 'externo';
  }

  private obtenhaTipoDispositivo(viewportWidth: number, userAgent: string) {
    const agente = (userAgent || '').toLowerCase();

    if (agente.includes('ipad') || (viewportWidth && viewportWidth >= 768 && viewportWidth < 1024)) {
      return 'tablet';
    }

    if (agente.includes('mobile') || (viewportWidth && viewportWidth < 768)) {
      return 'mobile';
    }

    return 'desktop';
  }

  private obtenhaBrowser(userAgent: string) {
    const agente = (userAgent || '').toLowerCase();

    if (agente.includes('edg/')) return 'Edge';
    if (agente.includes('opr/') || agente.includes('opera')) return 'Opera';
    if (agente.includes('chrome/')) return 'Chrome';
    if (agente.includes('safari/') && !agente.includes('chrome/')) return 'Safari';
    if (agente.includes('firefox/')) return 'Firefox';

    return 'Desconhecido';
  }

  private obtenhaSistemaOperacional(userAgent: string) {
    const agente = (userAgent || '').toLowerCase();

    if (agente.includes('android')) return 'Android';
    if (agente.includes('iphone') || agente.includes('ipad') || agente.includes('ios')) return 'iOS';
    if (agente.includes('win')) return 'Windows';
    if (agente.includes('mac')) return 'macOS';
    if (agente.includes('linux')) return 'Linux';

    return 'Desconhecido';
  }

  private obtenhaDominio(url: string) {
    if (!url) {
      return null;
    }

    try {
      return new URL(url).hostname;
    } catch (e) {
      return null;
    }
  }

  private obtenhaPrimeiroValor(valores: any[] = []) {
    const encontrado = valores.find((valor: any) => valor != null && String(valor).trim() !== '');
    return encontrado != null ? String(encontrado).trim() : null;
  }

  private arredondeNumero(valor: any) {
    if (valor == null || valor === '') {
      return undefined;
    }

    const numero = Number(valor);

    if (Number.isNaN(numero)) {
      return undefined;
    }

    return Math.round(numero * 100) / 100;
  }

  private leiaStorageJson(chave: string) {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const valor = localStorage.getItem(chave);
      return valor ? JSON.parse(valor) : null;
    } catch (e) {
      return null;
    }
  }

  private salveStorageJson(chave: string, valor: any) {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem(chave, JSON.stringify(valor));
    } catch (e) {
      // noop
    }
  }

  private removaValoresVazios(payload: any): any {
    if (Array.isArray(payload)) {
      return payload
        .map((item: any) => this.removaValoresVazios(item))
        .filter((item: any) => item != null && !(typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length === 0));
    }

    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    return Object.keys(payload).reduce((acc: any, chave: string) => {
      const valor = payload[chave];

      if (valor == null) {
        return acc;
      }

      if (typeof valor === 'string' && valor.trim() === '') {
        return acc;
      }

      if (Array.isArray(valor)) {
        const itens = this.removaValoresVazios(valor);

        if (!itens.length) {
          return acc;
        }

        acc[chave] = itens;
        return acc;
      }

      if (typeof valor === 'object') {
        const objeto = this.removaValoresVazios(valor);

        if (!Object.keys(objeto).length) {
          return acc;
        }

        acc[chave] = objeto;
        return acc;
      }

      acc[chave] = valor;
      return acc;
    }, {});
  }

  private carregueScript(scriptUrl: string) {
    const scriptId = 'openpanel-sdk-script';

    if (document.getElementById(scriptId)) {
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.defer = true;
    script.src = scriptUrl;
    document.head.appendChild(script);
  }

  private reinicieSdk() {
    const script = document.getElementById('openpanel-sdk-script');

    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }

    delete window.openpanel;
    delete window.op;
    this.clientIdAtual = null;
  }

  private garantaQueueGlobal() {
    if (window.op) {
      return;
    }

    const fila: any[] = [];
    const proxy = function () {
      if (arguments.length) {
        fila.push([].slice.call(arguments));
      }
    };

    proxy.q = fila;
    window.op = proxy;
  }
}
